/* eslint-disable react/no-unstable-nested-components */
import { Collapse } from 'antd';
import _ from 'lodash';
import { forwardRef } from 'react';
import {
  RadioInput,
  CheckboxInput,
  SelectInput,
  IconArrowCollapse as CollapseArowIcon,
  IconArrowExpand as ExpandArrowIcon,
} from '@/shared/ui';
import useReportSettings from './useReportSettings';
import './styles.scss';

const { Panel } = Collapse;

export type ReportSettingsRef = {
  getReportConfiguration: () => {
    ecgViewer: {
      gain: number | undefined;
      hpFilter: number | undefined;
      lpFilter: number | undefined;
    };
    longestRunBasedOn: string;
    sections: string[];
  };
};

/**
 * Collapsible settings panel controlling what the generated PDF report contains and how its ECG strips are drawn. useReportSettings, bound to the forwarded ref, supplies the section checkboxes plus gain, low-pass and high-pass option lists, and a third panel chooses whether the Longest VE/SVE Run event is picked by event duration or by beat count.
 *
 * @param props - No settings are read from props; the panel is driven entirely through the forwarded ref.
 * @param ref - Ref exposing the panel's current settings to the parent report tab.
 * @returns The rendered collapse with its report-sections, ECG-strip-configuration and Longest VE/SVE Run panels.
 */
const ReportSettings = forwardRef<ReportSettingsRef>((props, ref) => {
  const {
    gainOptions,
    hpFilterOptions,
    lpFilterOptions,
    state,
    onChangeCheckbox,
    onChangeSelect,
    checkboxRender,
  } = useReportSettings(ref);

  return (
    <div className="report-settings">
      <Collapse
        expandIconPosition="end"
        expandIcon={({ isActive }) => (
          <img src={isActive ? ExpandArrowIcon : CollapseArowIcon} alt="arrow icon" />
        )}
      >
        <Panel id="report-sections-collapse-panel" key="1" header="Report sections">
          <div className="select-section-report">
            <div className="title">Select the sections that you want to include in the report:</div>
            {_.map(checkboxRender, (item) => (
              <CheckboxInput
                id={item.name}
                name={item.name}
                data={item.data as any} // pre-existing bug: CheckboxInput's `data.value` is typed string|number, this is boolean
                checked={item.checked}
                onChange={onChangeCheckbox as any} // pre-existing bug: value is fed as boolean (from data.value bug) instead of string|number
              />
            ))}
          </div>
        </Panel>
        <Panel
          id="ecg-strips-config-collapse-panel"
          key="2"
          header="ECG captured strips configuration"
        >
          <div className="ecg-captured-strips">
            <div className="ecg-captured-strips__item">
              <div className="ecg-captured-strips__item-title">Gain</div>
              <SelectInput
                id="gain-select-input"
                name="gain"
                options={gainOptions}
                placeholder="Select gain"
                value={state.gain}
                onChange={onChangeSelect}
                blurInputOnSelect
                menuPlacement="top"
              />
            </div>
            <div className="ecg-captured-strips__item">
              <div className="ecg-captured-strips__item-title">Low pass filter</div>
              <SelectInput
                id="lp-filter-select-input"
                name="lpFilter"
                options={lpFilterOptions}
                placeholder="Select low pass filter"
                value={state.lpFilter}
                onChange={onChangeSelect}
                blurInputOnSelect
                menuPlacement="top"
              />
            </div>
            <div className="ecg-captured-strips__item">
              <div className="ecg-captured-strips__item-title">High pass filter</div>
              <SelectInput
                id="hp-filter-select-input"
                name="hpFilter"
                options={hpFilterOptions}
                placeholder="Select high pass filter"
                value={state.hpFilter}
                onChange={onChangeSelect}
                blurInputOnSelect
                menuPlacement="top"
              />
            </div>
          </div>
        </Panel>
        <Panel id="longest-ve-sve-run-collapse-panel" key="3" header="Longest VE/SVE Run">
          <div className="longest-ve-sve-run">
            <div className="longest-ve-sve-run__info">
              Select how the Longest VE/SVE Run event is determined.
            </div>
            <div className="longest-ve-sve-run__option">
              <RadioInput
                id="based-on-event-duration"
                name="longestRunBasedOn"
                data={{ label: 'Based on event duration', value: 'duration' }}
                checked={state.longestRunBasedOn === 'duration'}
                onChange={onChangeSelect}
              />
              <RadioInput
                id="based-on-beat-count"
                name="longestRunBasedOn"
                data={{ label: 'Based on beat count', value: 'beats' }}
                checked={state.longestRunBasedOn === 'beats'}
                onChange={onChangeSelect}
              />
            </div>
          </div>
        </Panel>
      </Collapse>
    </div>
  );
});

export default ReportSettings;
